import { InMemoryCacheConfig } from '@apollo/client';
import {
  PaginatedTemplates,
  PaginatedTemplateSetQuery,
  PaginatedTemplateSets,
  PaginatedTemplatesQuery,
} from '@fluentsms/fluent-client-documents';
import { QuerySearchRepositoryTemplatesArgs, RepositoryTemplateConnection } from '@fluentsms/fluent-sdk-graphql';
import { isUpperEnvironment } from '@fluentsms/utils';
import cuid from 'cuid';
import {
  Adjustment,
  BaseOrder,
  ClausePackageConnection,
  ClausePackageDomainConnectionArgs,
  ClauseTemplateConnection,
  CompanySummaryConnection,
  ContactWithCompanySummaryConnection,
  OrderSummaryConnection,
  QueryClauseTemplatesArgs,
  QueryGetOrderSummariesArgs,
  QueryGetPaginatedDirectoryCompaniesArgs,
  QueryGetPaginatedDirectoryContactsArgs,
  QueryGetSequencesArgs,
  Sequence,
} from './__graphql_generated__';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const applyKeyFields = ({ types, policy }: { types: string[]; policy: any }) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  types.reduce((obj: any, type: string) => {
    obj[type] = { keyFields: policy };
    return obj;
  }, {});

const cacheWithOrderId = ({ id, orderId, __typename }: { id: string; orderId: string; __typename: string }) => {
  if (!orderId || !id) {
    const orderIdSegmentA = !orderId && !id ? 'and' : '';
    const orderIdSegmentB = !orderId ? 'orderId' : '';

    if (!isUpperEnvironment()) {
      console.warn(
        `Query requested using type ${__typename}, but missing ${
          !id ? 'id' : ''
        } ${orderIdSegmentA} ${orderIdSegmentB}`,
      );
    }
    return !orderId && !id ? `${__typename}` : `${__typename}:${!orderId ? id : orderId}`;
  }
  return `${__typename}:${orderId}:${id}`;
};

export const cacheConfiguration: InMemoryCacheConfig = {
  typePolicies: {
    ...applyKeyFields({
      types: [
        'Buyer',
        'Seller',
        'OrderHeader',
        'OrderLoan',
        'OrderParty',
        'OrderPIQ',
        'OrderBuyerSellerSORepPayload',
        'OrderBuyerSellerSORep',
        'ProrationCharge',
        'Payoff',
        'RecordingFeeCalculatorLineItem',
        'TransferTaxCalculatorLineItem',
        'ClosingInformation',
        'ClosingQuestions',
        'ClosingCosts',
        'EstimatedTaxesInsuranceAssessments',
        'EstimatedTaxesInsuranceAssessmentsItem',
        'GfeCharge',
        'LoanDisclosures',
      ],
      policy: cacheWithOrderId,
    }),
    ...applyKeyFields({
      types: ['Fund'],
      policy: ['orderId'],
    }),
    PolicyRates: {
      keyFields: false,
    },
    PolicyTypeEndorsement: {
      keyFields: false,
    },
    TitlePremiumEndorsement: {
      keyFields: false,
    },
    AdditionalQuestionAnswerOption: {
      keyFields: false,
    },
    TitleSearchOrder: {
      fields: {
        importData: {
          merge(existing, incoming) {
            // If incoming is empty or missing inner arrays but existing has data, keep existing
            if (
              existing &&
              (!incoming ||
                (incoming &&
                  (!incoming.exceptions || incoming.exceptions.length === 0) &&
                  (!incoming.general || incoming.general.length === 0) &&
                  (!incoming.requirements || incoming.requirements.length === 0)))
            ) {
              return existing;
            }
            return incoming;
          },
        },
      },
    },
    User: {
      fields: {
        loginInfo: {
          keyArgs: false,
          merge(existing, incoming) {
            return { ...existing, ...incoming };
          },
        },
      },
    },
    Task: {
      fields: {
        assignedTo: {
          keyArgs: false,
          merge(existing, incoming) {
            if (!incoming || cuid.isCuid(incoming)) {
              // If the value for the "assignedTo" field is in the state where it temporarily shows as ID,
              // just show the previous "assignedTo" value until it "finishes."
              return existing;
            }

            return incoming;
          },
        },
      },
    },
    Document: {
      fields: {
        description: {
          read(description = ''): string {
            return description;
          },
        },
      },
    },
    Mutation: {
      fields: {
        deletePayoff: {
          keyArgs: false,
          merge(_, incoming, { args, cache }) {
            cache.modify({
              fields: {
                getDcAdjustments(prev, { readField }) {
                  const result = prev.adjustments.filter(
                    (adjRef: Adjustment) => args?.payoffId !== readField('externalItemId', adjRef),
                  );
                  return { adjustments: result };
                },
              },
            });
            cache.gc();
            return incoming;
          },
        },
      },
    },
    Query: {
      fields: {
        searchOrders: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          merge(_existing: BaseOrder[] = [], incoming: BaseOrder[]): BaseOrder[] {
            return incoming;
          },
        },
        getOrderSummaries: {
          // Don't cache separate results based on
          // any of this field's arguments.
          keyArgs: false,
          // Concatenate the incoming edges with
          // the existing edges.
          merge(
            existing: OrderSummaryConnection,
            incoming: OrderSummaryConnection,
            { args }: { args: QueryGetOrderSummariesArgs | null },
          ): OrderSummaryConnection {
            if (!args?.after) return incoming;
            const { edges: existingEdges } = existing ?? {};
            const { pageInfo, edges: incomingEdges } = incoming;

            return { pageInfo, edges: [...(existingEdges ?? []), ...(incomingEdges ?? [])] };
          },
        },
        getOrderSummariesV2: {
          // Don't cache separate results based on
          // any of this field's arguments.
          keyArgs: false,
          // Concatenate the incoming edges with
          // the existing edges.
          merge(
            existing: OrderSummaryConnection,
            incoming: OrderSummaryConnection,
            { args }: { args: QueryGetOrderSummariesArgs | null },
          ): OrderSummaryConnection {
            if (!args?.after) return incoming;
            const { edges: existingEdges } = existing ?? {};
            const { pageInfo, edges: incomingEdges } = incoming;

            return { pageInfo, edges: [...(existingEdges ?? []), ...(incomingEdges ?? [])] };
          },
        },
        getTemplatesV2: {
          // Don't cache separate results based on
          // any of this field's arguments.
          keyArgs: false,
          merge(
            existing: PaginatedTemplates,
            incoming: PaginatedTemplates,
            { args: { input } }: { args: { input: PaginatedTemplatesQuery } },
          ): PaginatedTemplates {
            if (!input?.after) return incoming;
            const { templates: existingTemplates } = existing ?? {};
            const { templates: incomingTemplates, ...incomingData } = incoming;

            return { ...incomingData, templates: [...(existingTemplates ?? []), ...(incomingTemplates ?? [])] };
          },
        },
        getTemplateSetsV2: {
          // Don't cache separate results based on
          // any of this field's arguments.
          keyArgs: false,
          merge(
            existing: PaginatedTemplateSets,
            incoming: PaginatedTemplateSets,
            { args: { input } }: { args: { input: PaginatedTemplateSetQuery } },
          ): PaginatedTemplateSets {
            if (!input?.after) return incoming;
            const { sets: existingSets } = existing ?? {};
            const { sets: incomingSets, ...incomingData } = incoming;

            return { ...incomingData, sets: [...(existingSets ?? []), ...(incomingSets ?? [])] };
          },
        },
        searchRepositoryTemplates: {
          // Don't cache separate results based on
          // any of this field's arguments.
          keyArgs: false,
          merge(
            existing: RepositoryTemplateConnection,
            incoming: RepositoryTemplateConnection,
            { args }: { args: QuerySearchRepositoryTemplatesArgs | null },
          ): RepositoryTemplateConnection {
            if (!args?.after) return incoming;
            const { edges: existingEdges } = existing ?? {};
            const { pageInfo, edges: incomingEdges } = incoming;

            return { pageInfo, edges: [...(existingEdges ?? []), ...(incomingEdges ?? [])] };
          },
        },
        getPaginatedDirectoryCompanies: {
          // Don't cache separate results based on
          // any of this field's arguments.
          keyArgs: false,
          // Concatenate the incoming edges with
          // the existing edges.
          merge(
            existing: CompanySummaryConnection,
            incoming: CompanySummaryConnection,
            { args }: { args: QueryGetPaginatedDirectoryCompaniesArgs | null },
          ): CompanySummaryConnection {
            if (!args?.after) return incoming;
            const { edges: existingEdges } = existing ?? {};
            const { pageInfo, edges: incomingEdges } = incoming;

            return { pageInfo, edges: [...(existingEdges ?? []), ...(incomingEdges ?? [])] };
          },
        },
        getPaginatedDirectoryContacts: {
          // Don't cache separate results based on
          // any of this field's arguments.
          keyArgs: false,
          // Concatenate the incoming edges with
          // the existing edges.
          merge(
            existing: ContactWithCompanySummaryConnection,
            incoming: ContactWithCompanySummaryConnection,
            { args }: { args: QueryGetPaginatedDirectoryContactsArgs | null },
          ): ContactWithCompanySummaryConnection {
            if (!args?.after) return incoming;
            const { edges: existingEdges } = existing ?? {};
            const { pageInfo, edges: incomingEdges } = incoming;

            return { pageInfo, edges: [...(existingEdges ?? []), ...(incomingEdges ?? [])] };
          },
        },
        getSequences: {
          merge(
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            _existing: Sequence[] = [],
            incoming: Sequence[],
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            _options: { args: QueryGetSequencesArgs | null },
          ): Sequence[] {
            // For order number sequences, always return the freshest list
            return incoming;
          },
        },
        fluentClauseTemplates: {
          // Don't cache separate results based on
          // any of this field's arguments.
          keyArgs: false,
          // Concatenate the incoming edges with
          // the existing edges.
          merge(
            existing: ClauseTemplateConnection,
            incoming: ClauseTemplateConnection,
            { args }: { args: QueryClauseTemplatesArgs | null },
          ): ClauseTemplateConnection {
            if (!args?.after) return incoming;
            const { edges: existingEdges } = existing ?? {};
            const { pageInfo, edges: incomingEdges } = incoming;

            return { pageInfo, edges: [...(existingEdges ?? []), ...(incomingEdges ?? [])] };
          },
        },
        clauseTemplates: {
          // ANP-2911 Update KeyArgs to look at specific fields so that
          // The Clause Drawers in Title don't open with the same result.
          keyArgs: ['filters', 'search', 'sort'],

          // Concatenate the incoming edges with
          // the existing edges.
          merge(
            existing: ClauseTemplateConnection,
            incoming: ClauseTemplateConnection,
            { args }: { args: QueryClauseTemplatesArgs | null },
          ): ClauseTemplateConnection {
            if (!args?.after) return incoming;
            const { edges: existingEdges } = existing ?? {};
            const { pageInfo, edges: incomingEdges } = incoming;

            return { pageInfo, edges: [...(existingEdges ?? []), ...(incomingEdges ?? [])] };
          },
        },
      },
    },
    PolicyType: {
      keyFields: (obj) => `PolicyType:${obj.id}:${obj.policyType}`,
    },
    ClausePackageDomain: {
      fields: {
        connection: {
          // Don't cache separate results based on
          // any of this field's arguments.
          keyArgs: false,
          // Concatenate the incoming edges with
          // the existing edges.
          merge(
            existing: ClausePackageConnection,
            incoming: ClausePackageConnection,
            { args }: { args: ClausePackageDomainConnectionArgs | null },
          ): ClausePackageConnection {
            if (!args?.after) return incoming;
            const { edges: existingEdges } = existing ?? {};
            const { pageInfo, edges: incomingEdges } = incoming;

            return { pageInfo, edges: [...(existingEdges ?? []), ...(incomingEdges ?? [])] };
          },
        },
      },
    },
  },
};
